import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Card from '../Card';
import { useRoomAttachmentsQuery } from '../../../../api/roomAttachments';
import Btn, { btnTypes } from '../../../../../../components/btn';
import { CardPagination } from '../../../../../../components/Card';
import { useOffsetPagination } from '../../../../../../hooks/useOffsetPagination';
import FetchDataWrapper from '../../../../../../components/FetchDataWrapper';
import { useFetchRoomAttachment } from '../../../../../../queries/roomAttachment';
import AttachmentsManager from '../../../../../../components/attachmentsManager';
import AttachmentActions from './ui/AttachmentActions';
import AttachmentChildren from './ui/AttachmentChildren';
import { useModalMethods } from '../../../../shared/packages/modalManager';
import { modalNames } from '../../../../shared/configs/modal';
import { roomAttachmentModalActions } from '../../../../shared/configs/attachments';
import {
  UnilateralAttachmentsProvider,
  useUnilateralAttachmentsAction,
} from './services/unilateralAttachments';
import { useUserAcceptanceRole } from '../../../../shared/hooks/userAcceptanceRole';
import { selectors, sliceKeys } from '../../../../model';
import styles from './index.module.scss';

const { selectSelectedRecordId, selectSelectedDocType, selectSearch } =
  selectors[sliceKeys.attachmentsFilter];

export const emptyArr = [];
export const DOCUMENTS_SIZE = 4;

const AttachmentsCard = ({ room }) => {
  const role = useUserAcceptanceRole();

  return (
    <Card>
      <UnilateralAttachmentsProvider>
        <Content addressId={room?.id} role={role} />
        {role.isDirectorOrManager && <Action room={room} />}
      </UnilateralAttachmentsProvider>
    </Card>
  );
};

export default AttachmentsCard;

function Content({ addressId, role }) {
  const { data, isFetching, refetch } = useRoomAttachmentsQuery({
    params: { addressId },
  });

  const attachments = Array.isArray(data) ? data : emptyArr;

  const selectedRecordId = useSelector(selectSelectedRecordId);
  const selectedDocType = useSelector(selectSelectedDocType);
  const search = useSelector(selectSearch);

  const unilateralAction = useUnilateralAttachmentsAction();

  const filteredAttachments = useMemo(() => {
    return attachments.filter((doc) => {
      if (selectedRecordId && doc.acceptanceClientId !== selectedRecordId)
        return false;
      if (selectedDocType && doc.type !== selectedDocType) return false;
      if (search && !doc.name.toLowerCase().includes(search.toLowerCase()))
        return false;
      return true;
    });
  }, [attachments, search, selectedDocType, selectedRecordId]);

  useEffect(() => {
    unilateralAction.set(attachments);
  }, [attachments, unilateralAction]);

  const { page, pageCount, next, prev, getDisplayItems } = useOffsetPagination({
    total: Array.isArray(filteredAttachments)
      ? filteredAttachments.length
      : undefined,
    pageSize: DOCUMENTS_SIZE,
    resetToFirstPage: false,
    controller: { defaultValue: 1 },
  });

  const displayItems = useMemo(() => {
    return getDisplayItems(filteredAttachments);
  }, [filteredAttachments, getDisplayItems]);

  const fetchAttachment = useFetchRoomAttachment(addressId);

  return (
    <>
      <Card.Header
        title="Документы"
        action={
          filteredAttachments.length > DOCUMENTS_SIZE && (
            <Pagination
              onPrev={prev}
              onNext={next}
              page={page}
              pageCount={pageCount}
            />
          )
        }
      />
      <FetchDataWrapper
        data={filteredAttachments}
        isFetching={isFetching}
        refetch={refetch}
        errMsg="Не удалось загрузить документы"
      >
        <Card.Body>
          <AttachmentsManager.Provider
            attachments={displayItems}
            getAttachment={fetchAttachment}
          >
            {displayItems.map((attachment) => (
              <AttachmentsManager.Card.Root
                key={attachment.id}
                attachment={attachment}
                shouldRenderMeta
                actions={
                  role.isDirectorOrManager ? (
                    <AttachmentActions addressId={addressId} />
                  ) : undefined
                }
                className={styles.attachment}
              >
                <AttachmentChildren attachment={attachment} parent="card" />
              </AttachmentsManager.Card.Root>
            ))}
          </AttachmentsManager.Provider>
        </Card.Body>
      </FetchDataWrapper>
    </>
  );
}

function Pagination({ onPrev, onNext, page, pageCount }) {
  return (
    <CardPagination
      onPrevClick={onPrev}
      onNextClick={onNext}
      currentPage={page}
      maxPage={pageCount}
    />
  );
}

function Action({ room }) {
  const { open } = useModalMethods();

  const handleClick = () => {
    open(modalNames.attachmentsForm, {
      actionType: roomAttachmentModalActions.addAttachment,
      room,
    });
  };

  return (
    <Card.Action>
      <Btn type={btnTypes.primaryInvert} block onClick={handleClick}>
        Добавить документ
      </Btn>
    </Card.Action>
  );
}
